import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faCircleCheck,
  faCircleExclamation,
  faCloudArrowUp,
  faFilePdf,
} from "@fortawesome/free-solid-svg-icons";

const API_URL = "https://globalbackend-expressjs.onrender.com/api/bootcamp/apply";
const MAX_RESUME_SIZE = 10 * 1024 * 1024;
const CURRENT_YEAR = new Date().getFullYear();

const GENDER_OPTIONS = ["Male", "Female", "Prefer not to say"];
const PROGRAMMING_LANGUAGE_OPTIONS = ["C#", ".Net", "Java", "Python", "Others"];
const EXPERIENCE_OPTIONS = ["Fresher", "0-1 Year", "1-2 Years", "2+ Years"];
const YES_NO_OPTIONS = ["Yes", "No"];
const INDONESIA_STATES = [
  "Aceh",
  "Bali",
  "Banten",
  "Bengkulu",
  "DKI Jakarta",
  "Gorontalo",
  "Jambi",
  "Jawa Barat",
  "Jawa Tengah",
  "Jawa Timur",
  "Kalimantan Barat",
  "Kalimantan Selatan",
  "Kalimantan Tengah",
  "Kalimantan Timur",
  "Kalimantan Utara",
  "Kepulauan Bangka Belitung",
  "Kepulauan Riau",
  "Lampung",
  "Maluku",
  "Maluku Utara",
  "Nusa Tenggara Barat",
  "Nusa Tenggara Timur",
  "Papua",
  "Papua Barat",
  "Papua Barat Daya",
  "Papua Pegunungan",
  "Papua Selatan",
  "Papua Tengah",
  "Riau",
  "Sulawesi Barat",
  "Sulawesi Selatan",
  "Sulawesi Tengah",
  "Sulawesi Tenggara",
  "Sulawesi Utara",
  "Sumatera Barat",
  "Sumatera Selatan",
  "Sumatera Utara",
  "Yogyakarta",
];

const INITIAL_FORM_DATA = {
  email: "",
  fullName: "",
  mobileNumber: "",
  currentCity: "",
  state: "",
  country: "Indonesia",
  gender: "",
  educationalQualification: "",
  graduationYear: "",
  primaryProgrammingLanguage: "",
  totalSoftwareDevelopmentExperience: "",
  handsOnExperience: "",
  declaration: false,
};

const FIELD_LABELS = {
  email: "Email",
  fullName: "Full Name",
  mobileNumber: "Mobile Number",
  currentCity: "Current City",
  state: "State",
  country: "Country",
  gender: "Gender",
  educationalQualification: "Educational Qualification",
  graduationYear: "Graduation Year",
  primaryProgrammingLanguage: "Primary Programming Language",
  totalSoftwareDevelopmentExperience: "Total Software Development Experience",
  handsOnExperience: "Hands-on experience",
  resume: "Resume",
  declaration: "Declaration",
};

// ---- Theme tokens (inline-style driven, per request) ----
const COLORS = {
  ink: "#16181d",
  subink: "#6b7280",
  faint: "#9ca3af",
  line: "#e6e8eb",
  panel: "#ffffff",
  canvas: "#f6f7f9",
  accent: "#001534",
  accentSoft: "#eaf1ee",
  accentSofter: "#f4f8f6",
  danger: "#b3413a",
  dangerSoft: "#fbebe9",
  success: "#001534",
  successSoft: "#eaf1ee",
};

const isEmail = (value) => /^\S+@\S+\.\S+$/.test(value.trim());
const isMobile = (value) => /^[\d\s()+-]{7,30}$/.test(value.trim());

const getFileError = (file) => {
  if (!file) return "Please upload your resume.";

  const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
  if (!isPdf) return "Please select a PDF resume.";
  if (file.size > MAX_RESUME_SIZE) return "Resume must be 10 MB or smaller.";

  return "";
};

const validateField = (name, value) => {
  const textValue = typeof value === "string" ? value.trim() : value;

  if (name === "resume") return getFileError(value);
  if (name === "declaration") return value === true ? "" : "Please accept the declaration.";
  if (!textValue) return `${FIELD_LABELS[name] || "This field"} is required.`;

  switch (name) {
    case "email":
      return isEmail(value) ? "" : "Enter a valid email address.";
    case "mobileNumber":
      return isMobile(value) ? "" : "Enter a valid mobile number.";
    case "graduationYear": {
      const year = Number(value);
      return Number.isInteger(year) && year >= 1900 && year <= CURRENT_YEAR
        ? ""
        : `Enter a year between 1900 and ${CURRENT_YEAR}.`;
    }
    case "gender":
      return GENDER_OPTIONS.includes(value) ? "" : "Please select a gender option.";
    case "state":
      return INDONESIA_STATES.includes(value) ? "" : "Please select a state.";
    case "primaryProgrammingLanguage":
      return PROGRAMMING_LANGUAGE_OPTIONS.includes(value)
        ? ""
        : "Please select a programming language.";
    case "totalSoftwareDevelopmentExperience":
      return EXPERIENCE_OPTIONS.includes(value) ? "" : "Please select your experience.";
    case "handsOnExperience":
      return YES_NO_OPTIONS.includes(value) ? "" : "Please select Yes or No.";
    default:
      return "";
  }
};

const formatFileSize = (bytes) => `${(bytes / (1024 * 1024)).toFixed(2)} MB`;

// ---- Shared field chrome ----
function Label({ htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} className="mb-1.5 block text-md font-medium" style={{ color: COLORS.ink }}>
      {children}
      <span style={{ color: COLORS.accent }} aria-hidden="true">
        {" "}
        *
      </span>
    </label>
  );
}

function ErrorText({ id, children }) {
  if (!children) return null;
  return (
    <p id={id} role="alert" className="mt-1.5 text-xs font-medium" style={{ color: COLORS.danger }}>
      {children}
    </p>
  );
}

function fieldStyle(hasError) {
  return {
    backgroundColor: COLORS.panel,
    borderColor: hasError ? COLORS.danger : COLORS.line,
    color: COLORS.ink,
  };
}

function Field({ id, label, error, helpText, children, span2 = false }) {
  return (
    <div className={span2 ? "sm:col-span-2" : ""}>
      <Label htmlFor={id} className="text-md font-medium">
        {label}
      </Label>
      {children}
      {helpText && !error && (
        <p className="mt-1.5 text-lg" style={{ color: COLORS.faint }}>
          {helpText}
        </p>
      )}
      <ErrorText id={`${id}-error`}>{error}</ErrorText>
    </div>
  );
}

function TextInput({ id, name, error, className = "", ...rest }) {
  return (
    <input
      id={id}
      name={name}
      aria-invalid={Boolean(error)}
      aria-describedby={error ? `${id}-error` : undefined}
      style={fieldStyle(error)}
      className={`w-full rounded-md border px-3 py-2.5 text-md font-normal outline-none transition-shadow duration-150 focus:shadow-[0_0_0_3px_var(--focus-ring)] ${className}`}
      {...rest}
    />
  );
}

function SelectInput({ id, name, value, options, placeholder, error, onChange, onBlur }) {
  return (
    <div className="relative">
      <select
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
        style={fieldStyle(error)}
        className="w-full appearance-none rounded-md border px-3 py-2.5 pr-9 text-md font-normal outline-none transition-shadow duration-150 focus:shadow-[0_0_0_3px_var(--focus-ring)]"
      >
        <option value="" disabled>
          {placeholder}
        </option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <FontAwesomeIcon
        icon={faChevronDown}
        aria-hidden="true"
        className="pointer-events-none absolute right-3 top-1/2 h-3 w-3 -translate-y-1/2"
        style={{ color: COLORS.faint }}
      />
    </div>
  );
}

function SectionHeading({ index, title, note }) {
  return (
    <div className="mb-5 flex items-baseline justify-between gap-3">
      <div className="flex items-baseline gap-2.5">
        <span className="text-xs font-semibold tabular-nums" style={{ color: COLORS.accent }}>
          {index}
        </span>
        <h2 className="text-[15px] font-semibold" style={{ color: COLORS.ink }}>
          {title}
        </h2>
      </div>
      {note && (
        <span className="text-xs" style={{ color: COLORS.faint }}>
          {note}
        </span>
      )}
    </div>
  );
}

export default function Bootcamp() {
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [resume, setResume] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  const updateError = (name, value) => {
    const error = validateField(name, value);
    setErrors((previousErrors) => {
      const nextErrors = { ...previousErrors };
      if (error) nextErrors[name] = error;
      else delete nextErrors[name];
      return nextErrors;
    });
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    const nextValue = type === "checkbox" ? checked : value;

    setFormData((previousData) => ({ ...previousData, [name]: nextValue }));
    if (errors[name]) updateError(name, nextValue);
    if (status) setStatus(null);
  };

  const handleBlur = (event) => {
    const { name } = event.target;
    updateError(name, formData[name]);
  };

  const handleResumeChange = (event) => {
    const file = event.target.files?.[0] || null;
    const error = getFileError(file);

    if (error) {
      setResume(null);
      event.target.value = "";
    } else {
      setResume(file);
    }

    setErrors((previousErrors) => {
      const nextErrors = { ...previousErrors };
      if (error) nextErrors.resume = error;
      else delete nextErrors.resume;
      return nextErrors;
    });
    setStatus(null);
  };

  const validateForm = () => {
    const nextErrors = {};
    Object.keys(INITIAL_FORM_DATA).forEach((name) => {
      const error = validateField(name, formData[name]);
      if (error) nextErrors[name] = error;
    });

    const resumeError = validateField("resume", resume);
    if (resumeError) nextErrors.resume = resumeError;

    return nextErrors;
  };

  const focusFirstInvalidField = (fieldName) => {
    const field =
      fieldName === "resume" ? document.getElementById("resume") : document.querySelector(`[name="${fieldName}"]`);

    field?.focus();
    field?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = validateForm();
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      focusFirstInvalidField(Object.keys(nextErrors)[0]);
      setStatus({ type: "error", message: "Please complete the highlighted fields." });
      return;
    }

    const payload = new FormData();
    Object.entries(formData).forEach(([name, value]) => {
      payload.append(name, typeof value === "boolean" ? String(value) : value);
    });
    payload.append("resumeOriginalName", resume.name);
    payload.append("resumeSize", String(resume.size));
    payload.append("resume", resume);

    setIsSubmitting(true);
    setStatus(null);

    try {
      await axios.post(API_URL, payload);
      toast.success("Application submitted successfully. Our team will contact you soon.");
      setFormData(INITIAL_FORM_DATA);
      setResume(null);
      setErrors({});
      setStatus({
        type: "success",
        message: "Application submitted successfully. Our team will contact you soon.",
      });
      const resumeInput = document.getElementById("resume");
      if (resumeInput) resumeInput.value = "";
    } catch (error) {
      setStatus({
        type: "error",
        message: error.response?.data?.message || "Something went wrong. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <Helmet>
      <title>Apply | Infor Cloud Suite Internal Talent Development Program | Kapil Tech</title>

      <meta
        name="description"
        content="Apply for the Infor Cloud Suite Internal Talent Development Program at Kapil Tech. Build expertise in ERP, MES, Infor CloudSuite, and smart manufacturing technologies."
      />

      <meta
        name="keywords"
        content="Infor Cloud Suite Program, ERP Training India, MES Training, Infor Careers, Infor CloudSuite Industrial, Smart Manufacturing, Software Engineer Program, IT Careers India, ERP Certification, Kapil Tech Careers, Infor Talent Development Program"
      />

      <meta name="author" content="Kapil Technologies" />
      <meta name="robots" content="index, follow" />

      {/* Open Graph */}
      <meta
        property="og:title"
        content="Apply | Infor Cloud Suite Internal Talent Development Program"
      />
      <meta
        property="og:description"
        content="Join the Infor Cloud Suite Internal Talent Development Program and build expertise in ERP, MES, and Infor technologies."
      />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="en_IN" />
      <meta
        property="og:url"
        content="https://ptkcs.com/career/infor-cloud-suite-bootcamp/apply"
      />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta
        name="twitter:title"
        content="Apply | Infor Cloud Suite Internal Talent Development Program"
      />
      <meta
        name="twitter:description"
        content="Apply for the Infor Cloud Suite Internal Talent Development Program at Kapil Tech."
      />

      {/* Canonical */}
      <link
        rel="canonical"
        href="https://ptkcs.com/career/infor-cloud-suite-bootcamp/apply"
      />

      {/* Favicon - use your colored logo */}
      <link rel="icon" type="image/png" href="/favicon.png" />
    </Helmet>
    <main
      className="font-jost min-h-screen px-4 py-10 sm:px-6 sm:py-14"
      style={{ backgroundColor: '#ffffff', "--focus-ring": COLORS.accentSoft }}
    >
      <div className="mx-auto max-w-3xl">
        {/* Header */}
        <div className="mb-8 sm:mb-10">
          <h1 className="text-[26px] font-semibold leading-tight sm:text-[30px]" style={{ color: COLORS.ink }}>
            Enterprise Cloud Suite Certification (Self - Investment)
          </h1>
          {/* <p className="mt-3 max-w-3xl text-base sm:text-lg leading-relaxed font-normal leading-6" style={{ color: '#000000', textAlign: "left" }}>
            Thank you for your interest in the Infor Cloud Suite Internal Talent Development Program.
          </p> */}
          <p className="max-w-3xl text-base sm:text-lg leading-relaxed font-normal leading-6" style={{ color: '#000000', textAlign: "left" }}>
            This program is designed for aspiring software engineers who want to build specialized expertise in ERP, IIoT, MES, Cloud, AI,
            and Infor Cloud Suite technologies.
          </p>
          <p className="max-w-3xl text-base sm:text-lg leading-relaxed font-normal leading-6" style={{ color: '#000000', textAlign: "left" }}>
          Shortlisted candidates will be contacted for the screening
            process.
          </p>
        </div>

        {/* Status banner */}
        {status && (
          <div
            className="mb-6 flex items-start gap-2.5 rounded-md border px-3.5 py-3 text-sm"
            style={{
              backgroundColor: status.type === "success" ? COLORS.successSoft : COLORS.dangerSoft,
              borderColor: status.type === "success" ? COLORS.success : COLORS.danger,
              color: status.type === "success" ? COLORS.success : COLORS.danger,
            }}
            role={status.type === "success" ? "status" : "alert"}
            aria-live="assertive"
          >
            <FontAwesomeIcon
              icon={status.type === "success" ? faCircleCheck : faCircleExclamation}
              className="mt-0.5 h-4 w-4 shrink-0"
              aria-hidden="true"
            />
            <span>{status.message}</span>
          </div>
        )}

        <div
          className="rounded-xl border"
          style={{ backgroundColor: COLORS.panel, borderColor: COLORS.line }}
        >
          <form noValidate onSubmit={handleSubmit} aria-busy={isSubmitting}>
            {/* 01 — Applicant info */}
            <div className="border-b px-5 py-6 sm:px-8 sm:py-7" style={{ borderColor: COLORS.line }}>
              <SectionHeading index="01" title="Applicant information" />
              <div className="grid gap-x-5 gap-y-5 sm:grid-cols-2">
                <Field id="fullName" label="Full Name" error={errors.fullName}>
                  <TextInput
                    id="fullName"
                    name="fullName"
                    type="text"
                    value={formData.fullName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoComplete="name"
                    error={errors.fullName}
                  />
                </Field>

                <Field id="email" label="Email" error={errors.email}>
                  <TextInput
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoComplete="email"
                    error={errors.email}
                  />
                </Field>

                <Field id="mobileNumber" label="Mobile Number" error={errors.mobileNumber}>
                  <TextInput
                    id="mobileNumber"
                    name="mobileNumber"
                    type="tel"
                    value={formData.mobileNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoComplete="tel"
                    inputMode="tel"
                    error={errors.mobileNumber}
                  />
                </Field>

                <Field id="gender" label="Gender" error={errors.gender}>
                  <SelectInput
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    options={GENDER_OPTIONS}
                    placeholder="Select gender"
                    error={errors.gender}
                  />
                </Field>

                <Field id="country" label="Country" error={errors.country}>
                  <TextInput
                    id="country"
                    name="country"
                    type="text"
                    value={formData.country}
                    disabled
                    readOnly
                    autoComplete="country-name"
                    error={errors.country}
                    className="cursor-not-allowed opacity-70"
                  />
                </Field>

                <Field id="state" label="State" error={errors.state}>
                  <SelectInput
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    options={INDONESIA_STATES}
                    placeholder="Select a state"
                    error={errors.state}
                  />
                </Field>

                <Field id="currentCity" label="Current City" error={errors.currentCity}>
                  <TextInput
                    id="currentCity"
                    name="currentCity"
                    type="text"
                    value={formData.currentCity}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoComplete="address-level2"
                    error={errors.currentCity}
                  />
                </Field>

                <Field id="educationalQualification" label="Educational Qualification" error={errors.educationalQualification}>
                  <TextInput
                    id="educationalQualification"
                    name="educationalQualification"
                    type="text"
                    value={formData.educationalQualification}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.educationalQualification}
                  />
                </Field>
              </div>
            </div>

            {/* 02 — Professional background */}
            <div className="border-b px-5 py-6 sm:px-8 sm:py-7" style={{ borderColor: COLORS.line }}>
              <SectionHeading index="02" title="Professional background" />
              <div className="grid gap-x-5 gap-y-5 sm:grid-cols-2">
                <Field id="graduationYear" label="Graduation Year" error={errors.graduationYear}>
                  <TextInput
                    id="graduationYear"
                    name="graduationYear"
                    type="number"
                    min="1900"
                    max={CURRENT_YEAR}
                    value={formData.graduationYear}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    inputMode="numeric"
                    error={errors.graduationYear}
                  />
                </Field>

                <Field id="primaryProgrammingLanguage" label="Primary Programming Language" error={errors.primaryProgrammingLanguage}>
                  <SelectInput
                    id="primaryProgrammingLanguage"
                    name="primaryProgrammingLanguage"
                    value={formData.primaryProgrammingLanguage}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    options={PROGRAMMING_LANGUAGE_OPTIONS}
                    placeholder="Select a language"
                    error={errors.primaryProgrammingLanguage}
                  />
                </Field>

                <Field
                  id="totalSoftwareDevelopmentExperience"
                  label="Total Software Development Experience"
                  error={errors.totalSoftwareDevelopmentExperience}
                >
                  <SelectInput
                    id="totalSoftwareDevelopmentExperience"
                    name="totalSoftwareDevelopmentExperience"
                    value={formData.totalSoftwareDevelopmentExperience}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    options={EXPERIENCE_OPTIONS}
                    placeholder="Select experience"
                    error={errors.totalSoftwareDevelopmentExperience}
                  />
                </Field>

                <Field id="handsOnExperience" label="Hands-on: C#, .NET, or Java?" error={errors.handsOnExperience}>
                  <SelectInput
                    id="handsOnExperience"
                    name="handsOnExperience"
                    value={formData.handsOnExperience}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    options={YES_NO_OPTIONS}
                    placeholder="Select Yes or No"
                    error={errors.handsOnExperience}
                  />
                </Field>
              </div>
            </div>

            {/* 03 — Resume */}
            <div className="border-b px-5 py-6 sm:px-8 sm:py-7" style={{ borderColor: COLORS.line }}>
              <SectionHeading index="03" title="Resume" note="PDF · Max 10 MB" />
              <label
                htmlFor="resume"
                className="flex cursor-pointer items-center gap-3.5 rounded-md border border-dashed px-4 py-4 transition-colors"
                style={{
                  borderColor: errors.resume ? COLORS.danger : COLORS.line,
                  backgroundColor: errors.resume ? COLORS.dangerSoft : COLORS.accentSofter,
                }}
              >
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md"
                  style={{ backgroundColor: COLORS.accentSoft, color: COLORS.accent }}
                >
                  <FontAwesomeIcon icon={resume ? faFilePdf : faCloudArrowUp} className="h-4 w-4" aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium" style={{ color: COLORS.ink }}>
                    {resume ? resume.name : "Choose a PDF resume"}
                  </span>
                  <span className="block text-xs" style={{ color: COLORS.subink }}>
                    {resume ? `${formatFileSize(resume.size)} selected` : "Click to browse"}
                  </span>
                </span>
                <span
                  className="shrink-0 rounded-md border px-3 py-1.5 text-xs font-semibold"
                  style={{ borderColor: COLORS.accent, color: COLORS.accent, backgroundColor: COLORS.panel }}
                >
                  Browse
                </span>
              </label>
              <input
                id="resume"
                name="resume"
                type="file"
                accept="application/pdf,.pdf"
                onChange={handleResumeChange}
                aria-invalid={Boolean(errors.resume)}
                aria-describedby={errors.resume ? "resume-error" : undefined}
                className="sr-only"
              />
              <ErrorText id="resume-error">{errors.resume}</ErrorText>
            </div>

            {/* 04 — Declaration + submit */}
            <div className="px-5 py-6 sm:px-8 sm:py-7">
              <label
                htmlFor="declaration"
                className="flex cursor-pointer items-start gap-3 rounded-md border p-3.5"
                style={{
                  borderColor: errors.declaration ? COLORS.danger : COLORS.line,
                  backgroundColor: errors.declaration ? COLORS.dangerSoft : COLORS.canvas,
                }}
              >
                <input
                  id="declaration"
                  name="declaration"
                  type="checkbox"
                  checked={formData.declaration}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  aria-invalid={Boolean(errors.declaration)}
                  aria-describedby={errors.declaration ? "declaration-error" : undefined}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded"
                  style={{ accentColor: COLORS.accent }}
                />
                <span className="text-sm leading-5" style={{ color: COLORS.subink }}>
                  <strong className="font-semibold" style={{ color: COLORS.ink }}>
                    Declaration
                  </strong>
                  <span style={{ color: COLORS.accent }} aria-hidden="true">
                    {" "}
                    *
                  </span>{" "}
                  — I confirm that the information provided above is accurate and complete to the best of my knowledge.
                </span>
              </label>
              <ErrorText id="declaration-error">{errors.declaration}</ErrorText>

              <button
                type="submit"
                disabled={isSubmitting}
                className="mt-5 inline-flex w-full items-center justify-center rounded-md px-5 py-2.5 text-sm font-semibold text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:min-w-44"
                style={{ backgroundColor: COLORS.accent }}
              >
                {isSubmitting ? "Submitting…" : "Submit Application"}
              </button>
            </div>
          </form>
        </div>

        <p className="mt-6 text-center text-xs" style={{ color: COLORS.faint }}>
          Fields marked with <span style={{ color: COLORS.accent }}>*</span> are required.
        </p>
      </div>
    </main>
    </>
  );
}
